/* drawlz-close-swap.js — in-place design switching in the SECOND CLOSE box,
 * for SIBLING-COLORWAY products (Expressionz, Originalz, Cottonz...).
 *
 * THE PROBLEM. On these products each colorway is a separate PRODUCT with its
 * own URL, so the second close's swatches are <a href> links. Clicking one
 * navigated: drawlz-pdp.js's initColorways adds ?size= and #second-close when a
 * size is already chosen, but with NO size chosen it bails WITHOUT
 * preventDefault, so the raw link fired and you landed at the TOP of a
 * different product page. Owner wants the box to just switch design in place.
 *
 * THE CONSTRAINT THAT SHAPES THIS FILE. drawlz-pdp.js adds to cart with
 *   body.set('id', state.variantId)
 * — the SHARED state, not the form's own input[name="id"]. So writing a variant
 * id into the second-close form is ignored, and there is exactly one shared
 * variantId for the whole page. Two consequences:
 *   1. the swap has to be corrected at FETCH time, not in the DOM;
 *   2. once the second close points at another design, the HERO button would
 *      otherwise add that other design too. The wrapper below therefore
 *      corrects the id in BOTH directions.
 * drawlz-pdp.js is 74 KB and cannot be edited (whole-file upserts only), so
 * everything here is an override: a capture-phase click listener that beats its
 * document-level handler, and a fetch wrapper. Same approach as the zodiac bump.
 *
 * SCOPE GUARDS. Exits unless the page has second-close colorway LINKS. Native
 * Color x Size products (Blessed Tee, SuedeSkin) have <button> swatches and no
 * hrefs, so they never enter — they are handled by drawlz-native-pdp.js. Hero
 * swatches keep navigating, which is correct: on the hero, picking a colorway
 * means going to that product's page.
 */
(function () {
  'use strict';

  var d = document;
  var CLOSE = '.drawlz-pdp-second-close';

  var box = null;          /* the second-close section */
  var mirror = null;       /* [data-drawlz-swatches-mirror] */
  var select = null;       /* the second close's size <select> */
  var idLine = null;       /* .drawlz-pdp-second-close__id */
  var labelEl = null;      /* the <b> holding the active design name */
  var ownDot = null;       /* the current product's own dot (a <span>) */

  var base = null;         /* snapshot of the ORIGINAL product's box */
  var override = null;     /* { handle, name, price, sizes: {SIZE: {id, ok}} } */
  var cache = {};          /* handle -> product json */
  var lastForm = null;     /* which form is mid-add */

  function money(cents) {
    /* matches drawlz-pdp.js's own multiplyPrice formatting */
    return '$' + (cents / 100).toLocaleString('en-US', {
      minimumFractionDigits: 2, maximumFractionDigits: 2
    });
  }

  function handleOf(href) {
    try {
      var path = new URL(href, location.origin).pathname;
      var m = path.match(/\/products\/([^\/?#]+)/);
      return m ? m[1] : null;
    } catch (e) { return null; }
  }

  function getProduct(handle) {
    if (cache[handle]) return Promise.resolve(cache[handle]);
    return fetch('/products/' + handle + '.js', {
      headers: { 'Accept': 'application/json' }
    }).then(function (r) {
      if (!r.ok) throw new Error('bad status');
      return r.json();
    }).then(function (p) { cache[handle] = p; return p; });
  }

  /* size -> {id, ok} off a product json. Sibling-colorway products are
     size-only, so option1 IS the size. */
  function sizeMap(p) {
    var map = {};
    (p.variants || []).forEach(function (v) {
      var s = v.options && v.options[0];
      if (!s) return;
      if (!map[s] || (!map[s].ok && v.available)) {
        map[s] = { id: v.id, ok: !!v.available };
      }
    });
    return map;
  }

  /* the size the shopper currently has, from whichever control shows it. The
     shared state keeps all four size controls on the same value. */
  function currentSize() {
    var pill = d.querySelector('[data-drawlz-size][aria-pressed="true"]');
    if (pill) return pill.getAttribute('data-size');
    if (select && select.value) return select.value;
    return null;
  }

  /* ---------- rendering the box ---------- */

  function writeOptions(map, order) {
    var ph = base.placeholder;
    var html = '<option value="">' + ph + '</option>';
    order.forEach(function (s) {
      var e = map[s];
      if (!e) return;
      html += '<option value="' + s.replace(/"/g, '&quot;') + '"'
        + ' data-variant-id="' + e.id + '"'
        + (e.ok ? '' : ' disabled')
        + '>' + s + (e.ok ? '' : base.soldOutSuffix) + '</option>';
    });
    select.innerHTML = html;
  }

  /* Pick the size to land on after a switch: keep the shopper's size when the
     new design has it purchasable, otherwise fall to that design's first
     available size. Landing on a REAL selection every time is what keeps the
     shared state, the CTA's enabled/disabled look and the cart in agreement —
     leaving the select blank would strand a stale size in the shared state. */
  function settleSize(map, order) {
    var want = currentSize();
    if (want && map[want] && map[want].ok) return want;
    for (var i = 0; i < order.length; i++) {
      if (map[order[i]] && map[order[i]].ok) return order[i];
    }
    return '';
  }

  function commit(size) {
    select.value = size || '';
    /* a real change event so drawlz-pdp.js's own handler reads the new
       data-variant-id and updates the shared state — we never write state. */
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function setRing(active) {
    if (!mirror) return;
    mirror.querySelectorAll('.drawlz-swatches__dot').forEach(function (el) {
      if (el === active) el.setAttribute('aria-current', 'page');
      else el.removeAttribute('aria-current');
    });
  }

  /* The current product's own dot is a <span> with no href, so it is inert by
     default and there would be no way back to the original design once you
     switched. While an override is live it becomes clickable. */
  function armOwnDot(on) {
    if (!ownDot) return;
    ownDot.style.cursor = on ? 'pointer' : '';
    if (on) ownDot.setAttribute('role', 'button');
    else ownDot.removeAttribute('role');
  }

  function applyOverride(link, p) {
    var map = sizeMap(p);
    var order = (p.options && p.options[0] && p.options[0].values) || Object.keys(map);
    override = {
      handle: p.handle,
      name: link.getAttribute('aria-label') || p.title,
      sizes: map
    };
    writeOptions(map, order);
    if (labelEl) labelEl.textContent = override.name.replace(/ — sold out$/, '');
    if (idLine) {
      var txt = base.idText.replace(/\$[\d,]+(\.\d+)?/, money(p.price));
      /* drawlz-pdp.js rebuilds this line from data-drawlz-base-id on every
         state emit, so the BASE has to move too or the change reverts. */
      idLine.setAttribute('data-drawlz-base-id', txt);
      idLine.textContent = txt;
    }
    setRing(link);
    armOwnDot(true);
    commit(settleSize(map, order));
  }

  function clearOverride() {
    override = null;
    select.innerHTML = base.selectHtml;
    if (labelEl) labelEl.textContent = base.labelText;
    if (idLine) {
      idLine.setAttribute('data-drawlz-base-id', base.idText);
      idLine.textContent = base.idText;
    }
    setRing(ownDot);
    armOwnDot(false);
    commit(settleSize(base.map, base.order));
  }

  /* ---------- add-to-cart correction ----------
     The single shared variantId cannot describe two designs at once, so the id
     is decided here, per form, at the moment of the request. */
  function resolveFor(form) {
    var size = currentSize();
    if (!size) return null;
    var inClose = !!(form && box && box.contains(form));
    var map = (inClose && override) ? override.sizes : base.map;
    var hit = map[size];
    return hit && hit.ok ? hit.id : null;
  }

  function wrapFetch() {
    var native = window.fetch;
    if (!native || native.__dzSwap) return;
    var wrapped = function (input, init) {
      try {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var body = init && init.body;
        if (override && body && typeof FormData !== 'undefined'
            && body instanceof FormData && /\/cart\/add/.test(url)) {
          var id = resolveFor(lastForm);
          if (id) body.set('id', String(id));
        }
      } catch (e) { /* never block a cart request */ }
      return native.apply(this, arguments);
    };
    wrapped.__dzSwap = true;
    window.fetch = wrapped;
  }

  /* ---------- wiring ---------- */

  /* ORDERING: the mirror is populated by drawlz-pdp.js's initSwatchMirror at
     DOMContentLoaded, so this must run AFTER it or it finds an empty mount and
     the guard below exits for good. Hence: loaded WITH defer (so it executes
     after drawlz-pdp.js and registers its DCL listener later), plus a retry on
     window load. `inited` is only set once wiring actually happened, so an
     early exit stays retryable. */
  var inited = false;

  function init() {
    if (inited) return;
    box = d.querySelector(CLOSE);
    if (!box) return;
    mirror = box.querySelector('[data-drawlz-swatches-mirror]');
    select = box.querySelector('[data-drawlz-size-select]');
    if (!mirror || !select) return;

    /* GUARD: sibling-colorway only. Native Color x Size products render
       <button> swatches with no href and must not enter. */
    var links = mirror.querySelectorAll('a[data-drawlz-swatch][href]');
    if (!links.length) return;

    inited = true;

    idLine = box.querySelector('.drawlz-pdp-second-close__id');
    labelEl = mirror.querySelector('.drawlz-swatches__label b');
    ownDot = mirror.querySelector('.drawlz-swatches__dot[aria-current]');

    /* snapshot the ORIGINAL box before anything is rewritten; the hero's own
       controls are never touched, so this stays the truth for hero adds. */
    var opts = Array.prototype.slice.call(select.options);
    var map = {}, order = [];
    opts.forEach(function (o) {
      if (!o.value) return;
      order.push(o.value);
      map[o.value] = { id: o.getAttribute('data-variant-id'), ok: !o.disabled };
    });
    base = {
      selectHtml: select.innerHTML,
      placeholder: (opts[0] && opts[0].textContent) || 'Select size',
      soldOutSuffix: ' — Sold out',
      labelText: labelEl ? labelEl.textContent : '',
      idText: idLine ? (idLine.getAttribute('data-drawlz-base-id') || idLine.textContent) : '',
      map: map,
      order: order
    };

    wrapFetch();

    /* remember which form is adding, so the fetch wrapper knows whose variant
       to resolve. Capture phase: runs before drawlz-pdp.js's own handler. */
    d.addEventListener('click', function (e) {
      var cta = e.target.closest && e.target.closest('[data-drawlz-atc]');
      if (cta) lastForm = cta.closest('form');
    }, true);

    /* CAPTURE phase on document beats initColorways's document-level bubble
       handler, so the link never navigates and no edit to drawlz-pdp.js is
       needed. Only close-origin swatches are intercepted. */
    d.addEventListener('click', function (e) {
      if (!e.target.closest) return;

      var back = e.target.closest('.drawlz-swatches__dot[role="button"]');
      if (back && override && mirror.contains(back)) {
        e.preventDefault();
        e.stopPropagation();
        clearOverride();
        return;
      }

      var link = e.target.closest('a[data-drawlz-swatch][href]');
      if (!link || link.getAttribute('data-origin') !== 'close') return;
      if (!mirror.contains(link)) return;
      /* let modified clicks open a new tab, as any link should */
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      /* dead in every size, or dead in the chosen size — drawlz-pdp.js marks
         both; do not switch to something unbuyable */
      if (link.classList.contains('is-sold-out')) return;

      e.preventDefault();
      e.stopPropagation();

      var handle = handleOf(link.getAttribute('href'));
      if (!handle) return;
      getProduct(handle).then(function (p) {
        applyOverride(link, p);
      }).catch(function () {
        /* network or parse failure: fall back to the old behaviour rather than
           leaving a dead swatch — navigate, keeping the shopper at the box. */
        var url = link.getAttribute('href');
        var size = currentSize();
        try {
          var u = new URL(url, location.origin);
          if (size) u.searchParams.set('size', size);
          u.hash = 'second-close';
          window.location.assign(u.toString());
        } catch (err) { window.location.assign(url); }
      });
    }, true);
  }

  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', init);
  else init();
  window.addEventListener('load', init);
}());
